/**
 * ThemeV2Card - Card component for displaying V2 theme in list view
 *
 * Shows theme name, color swatches, screen count, and action buttons.
 * Extracts background color from V2 Backdrop (kind: 'solid' → content.color).
 */

import React, { useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit, ContentCopy, Delete } from '@mui/icons-material';
import type { ThemeV2 } from '../types/themesV2.types';

/**
 * Extract background color from V2 Backdrop
 */
const getBackdropColor = (backdrop?: ThemeV2['background']): string => {
  if (backdrop?.kind === 'solid' && backdrop.content && 'color' in backdrop.content) {
    return (backdrop.content as { color: string }).color;
  }
  return '#1a1a1a';
};

export interface ThemeV2CardProps {
  /** V2 Theme data to display */
  theme: ThemeV2;
  /** Callback when Edit clicked */
  onEdit: (themeId: string) => void;
  /** Callback when Clone clicked */
  onClone: (themeId: string) => void;
  /** Callback when Delete clicked */
  onDelete: (themeId: string) => void;
}

/**
 * ThemeV2Card - Display a V2 theme in the themes list
 */
export const ThemeV2Card: React.FC<ThemeV2CardProps> = ({
  theme,
  onEdit,
  onClone,
  onDelete,
}) => {
  // Calculate screen count from FlowDocument
  const screenCount = useMemo(() => {
    return Object.keys(theme.flowDocument?.screens || {}).length;
  }, [theme.flowDocument?.screens]);

  // Format date for display
  const formattedDate = useMemo(() => {
    const date = new Date(theme.updatedAt);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, [theme.updatedAt]);

  // Handlers
  const handleEdit = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      onEdit(theme.id);
    },
    [onEdit, theme.id]
  );

  const handleClone = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClone(theme.id);
    },
    [onClone, theme.id]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(theme.id);
    },
    [onDelete, theme.id]
  );

  const handleCardClick = useCallback(() => {
    handleEdit();
  }, [handleEdit]);

  // Color swatches to display
  const colorSwatches = [
    { key: 'buttonsBackground', color: theme.colors.buttonsBackground },
    { key: 'titlesSubtitles', color: theme.colors.titlesSubtitles },
    { key: 'buttonsTitle', color: theme.colors.buttonsTitle },
  ];

  return (
    <Card
      data-testid={`theme-v2-card-${theme.id}`}
      onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      {/* Mini preview placeholder */}
      <Box
        data-testid={`theme-v2-preview-${theme.id}`}
        sx={{
          height: 160,
          bgcolor: getBackdropColor(theme.background),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" sx={{ color: theme.colors.titlesSubtitles }}>
          {screenCount} screen{screenCount !== 1 ? 's' : ''} (V2)
        </Typography>
      </Box>

      <CardContent>
        {/* Theme name with order */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h6" component="div">
            {theme.name}
          </Typography>
          {theme.order !== undefined && (
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {theme.order}
            </Typography>
          )}
        </Box>

        {/* Color swatches */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {colorSwatches.map(({ key, color }) => (
            <Tooltip key={key} title={key}>
              <Box
                data-testid={`v2-color-swatch-${key}`}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: color,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
            </Tooltip>
          ))}
        </Box>

        {/* Font family */}
        <Typography variant="body2" color="text.secondary">
          {theme.fonts.main.fontFamily}
        </Typography>

        {/* Screen count */}
        <Typography variant="body2" color="text.secondary">
          {screenCount} screen{screenCount !== 1 ? 's' : ''}
        </Typography>

        {/* Updated date */}
        <Typography variant="caption" color="text.secondary">
          Updated: {formattedDate}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={handleEdit}
            size="small"
            aria-label="Edit theme"
            data-testid={`v2-theme-edit-${theme.id}`}
          >
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clone">
          <IconButton
            onClick={handleClone}
            size="small"
            aria-label="Clone theme"
            data-testid={`v2-theme-clone-${theme.id}`}
          >
            <ContentCopy />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            onClick={handleDelete}
            size="small"
            aria-label="Delete theme"
            color="error"
            data-testid={`v2-theme-delete-${theme.id}`}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default ThemeV2Card;
